<template>
  <div class="fixed inset-0 z-[9999] flex items-end sm:items-end justify-center sm:justify-end p-0 sm:p-6 pointer-events-none">
    <!-- Backdrop for all screens -->
    <div class="fixed inset-0 bg-black/40 backdrop-blur-xs pointer-events-auto" @click="togglePopup"></div>

    <transition name="fade-slide">
      <div
        class="relative w-full sm:w-[440px] bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden border border-gray-200 flex flex-col max-h-[85vh] sm:max-h-[580px] z-10 font-sans pointer-events-auto mb-0 sm:mb-12"
      >
        <!-- Clean Minimal Header -->
        <div
          class="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 bg-white"
        >
          <div class="flex items-center gap-2.5">
            <h3 class="text-sm font-bold text-gray-900 mb-0">Help & Support</h3>
          </div>
          <div class="justify-end">
            <button
              type="button"
              @click="togglePopup"
              class="w-7 h-7 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 flex items-center justify-center border-none bg-transparent cursor-pointer transition-colors"
            >
              <v-icon small color="grey darken-1">mdi-close</v-icon>
            </button>
          </div>
        </div>

        <!-- Body -->
        <div class="p-5 overflow-y-auto flex-1 custom-scrollbar text-left">
          <!-- STEP 1: Select Type -->
          <div v-if="step === 1" class="flex flex-col gap-3">
            <p class="text-xs text-gray-500 mb-1">Select the type of assistance you need:</p>

            <div class="flex flex-col gap-2">
              <div
                v-for="type in types"
                :key="type"
                @click="handleSelectType(type)"
                class="p-3.5 rounded-xl border border-gray-200 hover:border-blue-500 hover:bg-blue-50/30 cursor-pointer transition-all flex items-center justify-between group"
              >
                <div class="flex items-center gap-3">
                  <v-icon small color="primary">{{ getCategoryIcon(type) }}</v-icon>
                  <span
                    class="text-xs font-semibold text-gray-800 group-hover:text-blue-600 transition-colors"
                  >
                    {{ formatCategoryName(type) }}
                  </span>
                </div>
                <v-icon x-small color="grey lighten-1" class="group-hover:text-blue-600"
                  >mdi-chevron-right</v-icon
                >
              </div>
            </div>
          </div>

          <!-- STEP 2: Contact Info -->
          <v-form v-else-if="step === 2" ref="form" class="flex flex-col gap-3">
            <p class="text-xs text-gray-500 mb-1">Please confirm your contact information:</p>

            <v-text-field
              v-model="companyName"
              label="Company Name *"
              outlined
              dense
              hide-details="auto"
              class="rounded-lg text-xs"
              :rules="[rules.required]"
            ></v-text-field>

            <v-text-field
              v-model="contactPerson"
              label="Your Name *"
              outlined
              dense
              hide-details="auto"
              class="rounded-lg text-xs"
              :rules="[rules.required]"
            ></v-text-field>

            <v-text-field
              v-model="contactEmail"
              label="Email Address *"
              outlined
              dense
              hide-details="auto"
              class="rounded-lg text-xs"
              :rules="[rules.required, rules.email]"
            ></v-text-field>

            <v-text-field
              v-model="phoneNumber"
              label="Mobile Number (10 Digits) *"
              outlined
              dense
              type="tel"
              maxlength="10"
              hide-details="auto"
              class="rounded-lg text-xs"
              :rules="[rules.required, rules.mobile]"
            ></v-text-field>

            <div class="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-gray-100">
              <v-btn text small color="grey darken-1" @click="prevStep">Back</v-btn>
              <v-btn
                color="primary"
                small
                depressed
                class="px-5 font-weight-bold"
                @click="nextStep"
              >
                Continue
              </v-btn>
            </div>
          </v-form>

          <!-- STEP 3: Issue Details -->
          <v-form v-else-if="step === 3" ref="form" class="flex flex-col gap-3">
            <div class="flex items-center justify-between mb-1">
              <span class="text-xs font-bold text-gray-800">Describe Your Request</span>
              <span class="text-[11px] font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                {{ formatCategoryName(selectedType) }}
              </span>
            </div>

            <v-text-field
              v-model="query"
              label="Subject *"
              placeholder="Brief summary"
              outlined
              dense
              hide-details="auto"
              class="rounded-lg text-xs"
              :rules="[rules.required]"
            ></v-text-field>

            <v-textarea
              v-model="detailsTemplate"
              label="Description *"
              placeholder="Please explain in detail..."
              outlined
              rows="3"
              auto-grow
              hide-details="auto"
              class="rounded-lg text-xs"
              :rules="[rules.required]"
            ></v-textarea>

            <!-- Attachment upload area -->
            <div>
              <div
                @dragover.prevent
                @dragenter.prevent
                @drop.prevent="handleDrop"
                :class="[
                  'border border-dashed rounded-xl p-3 text-center transition-all',
                  attachments.length >= 2
                    ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                    : 'border-gray-300 hover:border-blue-400 bg-gray-50/50 cursor-pointer'
                ]"
                @click="attachments.length < 2 && triggerFileInput()"
              >
                <p v-if="!fileNames.length" class="text-xs text-gray-500 mb-0">
                  <v-icon small color="grey darken-1" class="mr-1">mdi-paperclip</v-icon>
                  Attach screenshot (optional, max 2)
                </p>
                <div v-else class="flex flex-wrap gap-1.5 justify-center">
                  <span
                    v-for="(name, idx) in fileNames"
                    :key="idx"
                    class="text-[11px] bg-white border border-gray-200 text-gray-700 px-2 py-0.5 rounded shadow-xs"
                  >
                    📎 {{ name }}
                  </span>
                </div>
                <input
                  ref="fileInput"
                  type="file"
                  class="d-none"
                  multiple
                  accept="image/*"
                  @change="handleFileChange"
                  :disabled="attachments.length >= 2"
                />
              </div>
            </div>

            <div class="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-gray-100">
              <v-btn text small color="grey darken-1" @click="prevStep">Back</v-btn>
              <v-btn
                color="primary"
                small
                depressed
                :loading="isSubmitting"
                class="px-5 font-weight-bold"
                @click="handleSubmit"
              >
                Submit
              </v-btn>
            </div>
          </v-form>

          <!-- STEP 4: Success Message -->
          <div
            v-else-if="step === 4"
            class="flex flex-col items-center justify-center py-6 text-center"
          >
            <v-icon size="40" color="success" class="mb-2">mdi-check-circle-outline</v-icon>
            <h4 class="text-base font-bold text-gray-900 mb-1">Request Received</h4>
            <p class="text-xs text-gray-500 max-w-xs mb-5">
              Thank you. Our team has received your query and will get back to you shortly.
            </p>
            <v-btn
              color="primary"
              small
              depressed
              class="px-6 font-weight-bold"
              @click="togglePopup"
            >
              Close
            </v-btn>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'QueryComponent',
  data() {
    return {
      step: 1,
      query: '',
      selectedType: '',
      types: ['BOOK_A_DEMO', 'TECH_SUPPORT', 'COMPLAINT'],
      detailsTemplate: '',
      companyName: '',
      contactPerson: '',
      contactEmail: '',
      phoneNumber: '',
      attachments: [],
      fileNames: [],
      isSubmitting: false,
      rules: {
        required: (v) => !!v || 'This field is required',
        email: (v) => /.+@.+\..+/.test(v) || 'E-mail must be valid',
        mobile: (v) => (/^[0-9]{10}$/.test(v) && v.length === 10) || 'Please enter 10 digits'
      },
      orgId: '',
      tokenData: this.$storage?.getUniversal ? this.$storage.getUniversal('isAuthenticated') : localStorage.getItem('token')
    }
  },
  methods: {
    formatCategoryName(type) {
      if (type === 'BOOK_A_DEMO') return 'Book a Demo'
      if (type === 'TECH_SUPPORT') return 'Technical Support'
      if (type === 'COMPLAINT') return 'Report an Issue / Complaint'
      if (type === 'REQUEST_GUEST_CREDENTIAL') return 'Request Guest Credentials'
      return type?.replace(/_/g, ' ') || 'General Query'
    },
    getCategoryIcon(type) {
      if (type === 'BOOK_A_DEMO') return 'mdi-calendar-clock'
      if (type === 'TECH_SUPPORT') return 'mdi-laptop'
      if (type === 'COMPLAINT') return 'mdi-alert-circle-outline'
      if (type === 'REQUEST_GUEST_CREDENTIAL') return 'mdi-key-outline'
      return 'mdi-help-circle-outline'
    },
    handleSelectType(type) {
      if (type === 'BOOK_A_DEMO') {
        window.open('https://calendly.com/aditi-myfinfi', '_blank');
        this.togglePopup();
        return;
      }
      this.selectedType = type
      if (this.contactPerson && this.phoneNumber) {
        this.step = 3
      } else {
        this.step = 2
      }
    },
    triggerFileInput() {
      this.$refs.fileInput.click()
    },
    handleDrop(e) {
      const files = e.dataTransfer.files
      this.processFiles(files)
    },
    handleFileChange(e) {
      const files = e.target.files
      this.processFiles(files)
    },
    processFiles(files) {
      if (this.attachments.length + files.length > 2) {
        this.$toast.error('You can only upload up to 2 files.')
        return
      }
      for (let i = 0; i < files.length; i++) {
        if (files[i].type.startsWith('image/')) {
          this.attachments.push(files[i])
          this.fileNames.push(files[i].name)
        }
      }
    },
    togglePopup() {
      this.$emit('handleCloseComplaint', false)
    },
    nextStep() {
      if (this.$refs.form.validate()) {
        this.step++
      } else {
        this.$toast.error('Please fill out all required fields')
      }
    },
    prevStep() {
      if (this.step === 3 && this.contactPerson && this.phoneNumber) {
        this.step = 1
      } else {
        this.step--
      }
    },
    async handleSubmit() {
      if (this.$refs.form.validate()) {
        try {
          this.isSubmitting = true
          const formData = new FormData()
          this.attachments.forEach((file) => {
            formData.append('attachments', file)
          })
          formData.append('typeOfQuery', this.selectedType)
          formData.append('details', this.detailsTemplate)
          formData.append('companyName', this.companyName)
          formData.append('contactPerson', this.contactPerson)
          formData.append('contactEmail', this.contactEmail)
          formData.append('contactNumber', this.phoneNumber)
          formData.append('orgId', this.orgId)

          const response = await this.$axios.post('/cualitywork/query', formData)

          if (response.data.success || response.status === 200) {
            this.step = 4
            this.$toast.success(response?.data?.message || 'Query submitted successfully!')
          }
        } catch (error) {
          const errorMessage = error?.response?.data?.message || 'Something went wrong'
          this.$toast.error(errorMessage)
          console.error('Query submit error', error)
        } finally {
          this.isSubmitting = false
        }
      }
    },
    resetForm() {
      this.selectedType = ''
      this.detailsTemplate = ''
      this.companyName = ''
      this.contactPerson = ''
      this.contactEmail = ''
      this.phoneNumber = ''
      this.attachments = []
      this.fileNames = []
      this.step = 1
    }
  },
  mounted() {
    if (!this.tokenData) {
      this.types.push('REQUEST_GUEST_CREDENTIAL')
    }
    try {
      const orgData = JSON.parse(localStorage.getItem('orgData'))
      if (orgData) {
        this.companyName = orgData?.name || ''
        this.orgId = orgData?.id || ''
      }
    } catch (e) {
      console.warn('Error reading orgData in QueryComponent:', e)
    }
    try {
      const userRole = JSON.parse(localStorage.getItem('user'))
      if (userRole) {
        this.phoneNumber = String(userRole?.mobile || userRole?.user?.mobile || '')
        this.contactEmail = userRole?.email || userRole?.user?.email || ''
        const firstName = userRole?.first_name || userRole?.user?.first_name || ''
        const lastName = userRole?.last_name || userRole?.user?.last_name || ''
        this.contactPerson = `${firstName} ${lastName}`.trim()
      }
    } catch (e) {
      console.warn('Error reading user in QueryComponent:', e)
    }
  }
}
</script>

<style scoped>
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.2s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #e2e8f0;
  border-radius: 4px;
}
</style>
